// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// material ui icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// material ui
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

/**
 * ApiResponseAlert
 * Props:
 * - title?: string
 * - message: string | ReactNode
 * - object?: any (optional details shown when "see details" is toggled)
 * - severity?: 'error' | 'warning' | 'info' | 'success' (defaults to 'error')
 * - goBack?: boolean (when true shows a back action that navigates -1)
 * - onClose?: () => void (optional close handler to show an X close button)
 */
const ApiResponseAlert = ({
  title,
  message,
  object,
  severity = 'error',
  goBack = false,
  backPath = '..',
  onClose,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const toggleDetails = () => setShowDetails((s) => !s);

  const stringifyObject = (obj) => {
    if (obj == null) return '';
    try {
      if (typeof obj === 'string') return obj;
      if (obj instanceof Error)
        return `${obj.name}: ${obj.message}\n${obj.stack || ''}`;
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      try {
        return String(obj);
      } catch (_) {
        return '[unserializable object]';
      }
    }
  };

  const detailsText = object ? stringifyObject(object) : '';

  return (
    <Alert
      severity={severity}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {goBack && (
            <IconButton
              aria-label="go back"
              color="inherit"
              size="small"
              onClick={() => navigate(backPath)}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          {onClose && (
            <Button color="inherit" size="small" onClick={onClose}>
              {t('actions.close')}
            </Button>
          )}
        </Box>
      }
    >
      {title && <AlertTitle>{title}</AlertTitle>}

      <div>{message}</div>

      {object && (
        <Box mt={1}>
          <Button size="small" onClick={toggleDetails}>
            {showDetails ? t('actions.hide') : t('actions.show')}
          </Button>
          <Collapse in={showDetails}>
            <Box mt={1} sx={{ border: '1px solid', p: 1, borderRadius: 1 }}>
              <Typography
                component="pre"
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', m: 0 }}
              >
                {detailsText}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )}
    </Alert>
  );
};

export default ApiResponseAlert;
